import { useState } from "react";

import { documentService } from "@/app/providers/services";
import { AttributeIcon, GraphNodeType, getGraphNodeTypeColor, getNodeType, scrollableMixin } from "@/common";
import { SourceEditor, createSourcePlugins } from "@/common/editor/RuleAuthor/SourceEditor";
import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { Label } from "@/components";
import { resolveReleaseDocument } from "@/hooks/documentNames";

import { useEditAttributeModal } from "@/pages/models/release/DataModel/route/Attributes/AttributeEditModal";
import { AttributeIDField } from "@/pages/models/release/DataModel/route/Attributes/AttributeIDField";
import { AttributeUsage } from "@/pages/models/release/DataModel/route/Attributes/DocumentUsage";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import styled from "@emotion/styled";
import AttributeExplanation from "@imminently/decisively-ui/AttributeExplanation";
import Icon from "@imminently/decisively-ui/Icon";
import Button from "@imminently/decisively-ui/Button";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import Tabs, { type TabValue } from "@imminently/decisively-ui/Tabs";
import { Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import type { GraphNode, Trigger } from "@packages/commons";
import Toolbar from "@imminently/decisively-ui/Toolbar";
import { PlateProvider } from "@udecode/plate";
import { capitalize, find, get } from "lodash";
import { hexToHSL } from "../../theme";

const TabToolbar = styled(Tabs.List)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border: none !important;
  /* box-shadow: 0px -5px 10px 0px #0000001A; */

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const SourceContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  /* editor overrides to clean up padding */

  [data-slate-editor="true"] {
    padding: 0;

    > * {
      padding: 0;
    }
  }
`;

const SOURCE_IN_ATTR_POPUP_ID = "source-in-attr-popup";
const SourceContent = ({ node }) => {
  const release = useFullReleaseStrict();

  // Find the document that this is defined in
  if (!node || !node.definedIn) return null;
  const document = resolveReleaseDocument(release.documents, String(node.definedIn));
  const documentReference = document?.reference ?? "";
  const { data, isLoading } = documentService.useGetOne(documentReference, { enabled: Boolean(documentReference) });

  if (isLoading) {
    return <LoadingSpinner size="sm" />;
  }
  if (!data) {
    return <span>Error getting document</span>;
  }

  const sourceContents = (() => {
    if (get(data, ["contents", "sections", "length"], null) === 0) {
      // Special case there - are no sections.
      // Show the entire document?
      return get(data, ["contents", "source"], null);
    }

    // Look through the rules and find the conclusion for this node
    const conclusion = find(get(data, ["contents", "rules"], []), (el) => {
      if (el.type === "conclusion" && el.text.includes(node.description)) return true;
      else return false;
    });

    if (conclusion && conclusion.sectionId) {
      // Show this section
      const section = find(get(data, ["contents", "source"], []), { type: "section", id: conclusion.sectionId });
      // unpack the section as we only want the children
      if (section) return section.children;
    }

    return null;
    // return 'Not able to retrieve source';
  })();

  return (
    <SourceContentWrap>
      <AttributeDefinedIn
        node={node}
        showUsedIn={false}
      />

      {sourceContents === null ? null : (
        <PlateProvider
          id={SOURCE_IN_ATTR_POPUP_ID}
          plugins={createSourcePlugins()}
          initialValue={Array.isArray(sourceContents) ? sourceContents : [sourceContents]}
        >
          <SourceEditor
            id={SOURCE_IN_ATTR_POPUP_ID}
            editableProps={{ readOnly: true }}
          />
        </PlateProvider>
      )}
    </SourceContentWrap>
  );
};

export const Attribute = ({ node, children }: { node: GraphNode | null; children?: React.ReactNode }) => {
  const openEditModal = useEditAttributeModal();

  let nodeType = getNodeType(node);
  if (!node) {
    node = { id: "pending", entity: "Pending document save", type: "auto" };
    nodeType = GraphNodeType.MISSING;
  }

  const color = getGraphNodeTypeColor(nodeType as any);

  const darkColor = hexToHSL(color, 80);

  return (
    <>
      <AttributeContainer style={{ backgroundColor: color }}>
        <Tooltip
          title={node.type}
          placement="right"
        >
          <div
            data-icon
            style={{ backgroundColor: darkColor }}
          >
            <AttributeIcon
              type={node.type}
              nodeType={nodeType}
            />
          </div>
        </Tooltip>
        <Typography
          noWrap
          style={{ fontSize: "14px" }}
        >
          {capitalize(node.entity)}
        </Typography>
        {node.id === "pending" ? null : (
          <div
            data-actions
            className="flex items-center"
          >
            <Typography sx={{ fontSize: "14px !important", px: 2 }}>{capitalize(nodeType)}</Typography>
            <Toolbar.Divider />
            <Button icon
              label="Open edit modal"
              className="hover:bg-transparent"
              onClick={() => {
                if (node) {
                  openEditModal(node.id);
                }
              }}
            >
              <Icon name="open-in-new" />
            </Button>
          </div>
        )}
      </AttributeContainer>
    </>
  );
};

const TriggerInfo = ({ trigger }: { trigger: Trigger }) => {
  const graph = useParsedGraph();
  switch (trigger.type) {
    case "task": {
      const node = graph.node(trigger.outcome);
      return (
        <Typography variant="caption">
          {trigger.title} ➝ {node?.description}
        </Typography>
      );
    }
    case "enrichment":
      return <Typography variant="caption">{trigger.title}</Typography>;
    case "comms":
      return (
        <Typography variant="caption">
          {trigger.title} ({trigger.method})
        </Typography>
      );
    default:
      // @ts-ignore catch in case we add a type
      return <Typography variant="caption">UNSUPPORTED {trigger.type}</Typography>;
  }
};

const TypeToLabel = {
  task: "Tasks",
  enrichment: "Integrations",
  comms: "Communications",
};

const Triggers = ({ node }: { node: GraphNode }) => {
  if (!node.triggers) return null;

  // group triggers by type
  const groupedTriggers = node.triggers.reduce(
    (acc, trigger) => {
      if (!acc[trigger.type]) {
        acc[trigger.type] = [];
      }
      acc[trigger.type].push(trigger);
      return acc;
    },
    {} as Record<string, Trigger[]>,
  );

  return (
    <div className="flex flex-col gap-2">
      {Object.entries(groupedTriggers).map(([type, triggers]) => (
        <div
          key={type}
          className="flex flex-col gap-1"
        >
          <Typography
            style={{ fontSize: "0.625rem" }}
            variant="h6"
          >
            {TypeToLabel[type]}
          </Typography>
          <div className="flex flex-col">
            {(triggers as any).map((trigger, i) => (
              <TriggerInfo
                key={`${type}-${i}`}
                trigger={trigger}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const TabRoot = styled(Tabs)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TabContent = styled(Tabs.Content)`
  flex-direction: column;
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  outline: none;
  ${scrollableMixin};

  &[data-state="active"] {
    display: flex;
  }
`;

const AttributeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  align-items: center;
  justify-content: flex-start;
  color: var(--foreground);
  border-top: 1px solid var(--border);

  [data-icon] {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 2px solid var(--background);
    height: 2rem;
    width: 2rem;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  .MuiTypography-root {
    flex: 1;
    font-style: italic;
    font-size: 1rem;
  }

  [data-actions] {
    [class*=divider] {
      height: 1rem;
      margin: 0 0.25rem;
      background-color: var(--foreground);
    }

    .MuiIconButton-root {
      &:hover {
        background-color: #00000010;
      }
    }

    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

const AttributeDefinedIn = styled(AttributeUsage)`
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const RulesContent = ({ node }: { node: GraphNode }) => {
  return (
    <div
      className="flex flex-col"
      style={{ fontSize: "0.75rem" }}
    >
      {node.definedIn ? (
        <AttributeDefinedIn
          node={node}
          showUsedIn={false}
        />
      ) : null}
      <AttributeExplanation
        node={node}
        selectedNode={node}
      />
    </div>
  );
};

// #region main component

export interface AttributeSummaryCardProps {
  node: GraphNode;
  extraTabs?: Array<TabValue | null>;
  children?: React.ReactNode;
}

const AttributeSummaryCard = (props: AttributeSummaryCardProps) => {
  const { node, extraTabs, children } = props;

  if (!node) {
    return (
      <Attribute
        node={null}
      //parent={parent}
      >
        {children}
      </Attribute>
    );
  }

  const tabs = [
    ...(extraTabs || []),
    node.definedIn || node.usedIn
      ? {
        value: "docs",
        label: "Usage",
        icon: <Icon name="documents" />,
        content: <AttributeUsage node={node} />,
      }
      : null,
    node.definedIn
      ? {
        value: "source",
        label: "Sources",
        icon: <Icon name="document" />,
        content: <SourceContent node={node} />,
      }
      : null,
    {
      value: "rules",
      label: "Rules",
      icon: <Icon name="compare" />,
      content: <RulesContent node={node} />,
    },
    node.tags && node.tags.length > 0
      ? {
        value: "tags",
        label: "Tags",
        icon: <Icon name="label" />,
        content: (
          <div className="flex items-center gap-2 flex-wrap">
            {node.tags.map((t) => (
              <Label
                key={t}
                color="purpleSlate"
              >
                {t}
              </Label>
            ))}
          </div>
        ),
      }
      : null,
    node.explanation
      ? {
        value: "explanation",
        label: "Explanation",
        icon: <Icon name="tooltip" />,
        content: <Typography>{node.explanation}</Typography>,
      }
      : null,
    node.enum
      ? {
        value: "enum",
        label: "Enum",
        icon: <Icon name="boolean" />,
        content: <Typography>{node.enum}</Typography>,
      }
      : null,
    node.publicId
      ? {
        value: "pubicName",
        label: "Public Name",
        icon: <Icon name="code" />,
        content: <Typography>{node.publicId}</Typography>,
      }
      : null,
    node.triggers && node.triggers.length > 0
      ? {
        value: "triggers",
        label: "Triggers",
        icon: <Icon name="open-in-new" />,
        content: <Triggers node={node} />,
      }
      : null,
    {
      value: "guid",
      label: "GUID",
      icon: <Icon name="guid" />,
      content: <AttributeIDField id={node.id} />,
    },
  ].filter(Boolean) as TabValue[];

  return (
    <>
      <TabRoot defaultValue={tabs[0].value}>
        {tabs.map((tab) => (
          <TabContent
            key={`content-${tab.value}`}
            value={tab.value}
          >
            {tab.content}
          </TabContent>
        ))}
        <TabToolbar barPlacement={"top"}>
          {tabs.map((tab) => (
            <Tabs.Trigger
              asChild
              key={`tab-${tab.value}`}
              value={tab.value}
            >
              <Button icon label={tab.label}>{tab.icon}</Button>
            </Tabs.Trigger>
          ))}
        </TabToolbar>
      </TabRoot>
      <Attribute node={node} />
      {children}
    </>
  );
};

export default AttributeSummaryCard;

// #endregion
